import type { Cell, Graph } from '@maxgraph/core';
import type { OperationNodeSpec } from './types';

/**
 * CycleMembershipResolver — pure function that computes the induced subgraph
 * of cells belonging to a cycle.
 *
 * A cell belongs to cycle C iff it lies on at least one directed path from
 * C.startCellId to C.endCellId. Operates on a static snapshot (adjacency
 * lists) to avoid MaxGraph model-event reentrancy.
 */

export interface GraphEdge {
  id: string;
  sourceId: string;
  targetId: string;
}

export interface GraphSnapshot {
  /** All vertex IDs (materials + operations). */
  vertexIds: string[];
  /** All directed edges. */
  edges: GraphEdge[];
  /** Operation archetype by ID (to detect COMPLEX vs simple). */
  operationArchetypes?: Record<string, string>;
}

/**
 * Visual representation type for a cell in a cycle.
 * 'vertex' = COMPLEX operation or material (rendered as a cell)
 * 'edge' = Simple operation (TRANSFER/TRANSFORM/MEASURE, collapsed into an edge)
 */
export type VisualRepresentation = 'vertex' | 'edge';

export interface CycleDefinition {
  id: string;
  groupId: string;
  startCellId: string;
  endCellId: string;
  repeatCount: number;
}

/**
 * Compute the set of cell IDs that belong to the induced subgraph of a cycle.
 *
 * Algorithm:
 *   forward  = BFS from startCellId following outgoing edges
 *   backward = BFS from endCellId following incoming edges
 *   members  = forward ∩ backward
 *
 * Returns an empty set if start or end is missing from the snapshot, or if
 * there is no directed path between them.
 */
export function resolveCycleMembers(
  snapshot: GraphSnapshot,
  cycle: CycleDefinition,
): Set<string> {
  const { startCellId, endCellId } = cycle;
  const vertexSet = new Set(snapshot.vertexIds);

  if (!vertexSet.has(startCellId) || !vertexSet.has(endCellId)) {
    return new Set();
  }

  // Build adjacency lists
  const outgoing = new Map<string, string[]>();
  const incoming = new Map<string, string[]>();

  for (const edge of snapshot.edges) {
    if (!vertexSet.has(edge.sourceId) || !vertexSet.has(edge.targetId)) continue;

    if (!outgoing.has(edge.sourceId)) outgoing.set(edge.sourceId, []);
    outgoing.get(edge.sourceId)!.push(edge.targetId);

    if (!incoming.has(edge.targetId)) incoming.set(edge.targetId, []);
    incoming.get(edge.targetId)!.push(edge.sourceId);
  }

  // BFS forward from startCellId
  const forwardReachable = bfs(startCellId, outgoing);
  // BFS backward from endCellId (follow incoming edges)
  const backwardReachable = bfs(endCellId, incoming);

  // Intersection: cells on a path from start → end
  const members = new Set<string>();
  for (const cellId of forwardReachable) {
    if (backwardReachable.has(cellId)) {
      members.add(cellId);
    }
  }

  return members;
}

function bfs(start: string, adjacency: Map<string, string[]>): Set<string> {
  const visited = new Set<string>();
  const queue: string[] = [start];
  visited.add(start);

  while (queue.length > 0) {
    const current = queue.shift()!;
    const neighbors = adjacency.get(current) ?? [];
    for (const n of neighbors) {
      if (!visited.has(n)) {
        visited.add(n);
        queue.push(n);
      }
    }
  }

  return visited;
}

/**
 * Determine the visual representation of an operation ID.
 * COMPLEX operations render as vertices; simple operations (TRANSFER/TRANSFORM/MEASURE)
 * are collapsed into edges.
 */
export function getOperationVisualRepresentation(
  opId: string,
  snapshot: GraphSnapshot,
): VisualRepresentation {
  const archetype = snapshot.operationArchetypes?.[opId];
  if (archetype === 'COMPLEX') {
    return 'vertex';
  }
  // Simple operations (TRANSFER, TRANSFORM, MEASURE) are edges
  return 'edge';
}

/**
 * Compute visual bounds for a set of cycle member cells.
 * For COMPLEX operations: use cell geometry.
 * For simple operations: estimate bounds from connected materials.
 * For materials: use cell geometry.
 */
export interface VisualBounds {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function computeVisualBounds(
  graph: Graph,
  memberCellIds: string[],
  snapshot: GraphSnapshot,
): VisualBounds | null {
  if (memberCellIds.length === 0) return null;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let hasValidBounds = false;

  for (const cellId of memberCellIds) {
    const cell = graph.getDataModel().getCell(cellId);
    if (!cell) continue;

    const geo = cell.getGeometry();
    if (!geo) continue;

    hasValidBounds = true;
    minX = Math.min(minX, geo.x);
    minY = Math.min(minY, geo.y);
    maxX = Math.max(maxX, geo.x + geo.width);
    maxY = Math.max(maxY, geo.y + geo.height);
  }

  if (!hasValidBounds) return null;

  return {
    x: minX,
    y: minY,
    w: maxX - minX,
    h: maxY - minY,
  };
}


/**
 * Extract a GraphSnapshot from the store's graphData for use with
 * resolveCycleMembers(). Avoids touching MaxGraph model directly.
 */
export function buildSnapshotFromStore(graphData: {
  materialNodes: Record<string, { id?: string }>;
  operationNodes: Record<string, { id?: string; archetype?: string }>;
  flowEdges: Record<string, { id?: string; sourceId: string; targetId: string }>;
}): GraphSnapshot {
  const vertexIds: string[] = [
    ...Object.keys(graphData.materialNodes),
    ...Object.keys(graphData.operationNodes),
  ];
  const edges: GraphEdge[] = Object.entries(graphData.flowEdges).map(
    ([id, e]) => ({ id, sourceId: e.sourceId, targetId: e.targetId }),
  );

  // Build archetype map for identifying COMPLEX vs simple operations
  const operationArchetypes: Record<string, string> = {};
  for (const [opId, op] of Object.entries(graphData.operationNodes)) {
    if (op.archetype) {
      operationArchetypes[opId] = op.archetype;
    }
  }

  return { vertexIds, edges, operationArchetypes };
}
